import ArrowUp from "@/Icons/ArrowUp";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { FetchedTenantTypes } from "@/Utils/api/sanity/types";
import { TFormatNumberSuffix, formatWeight } from "@/Utils/formatFunctions";
import { getTenantUrl } from "@/Utils/getTenantUrl";
import { trpc } from "@/Utils/trpc";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import LoadingTable from "@/components/LoadingTable";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { FC, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";

import { FilterTimeFrameValue } from "server/config-api/index.js";
import { Customer } from "server/dwhApi/customers/types.js";
import { twMerge } from "tailwind-merge";
import { v4 } from "uuid";

import { ShareLink } from "../ShareLink";
import { Card } from "./card";
import { ActiveOptions } from "./filterSort/FilterSortContext";
import { OPTION_ALL, hasNonAllOptionActive } from "./filterSort/constants";
import { getTenantCategoryOptions } from "./filterSort/filterCreators";
import { createTimeFilters } from "./filterSort/filterCreators";
import { getTimeFrame } from "./filterSort/filterTimeFrame";
import { FilterSort, FilterSortMenuType } from "./filterSort/types";

const printWasteType = (customer: Customer, wasteType: string) => {
  const wtValue = customer.wasteTypes.find((el) => el.code === wasteType)
    ?.weight.quantity;
  const wtUnit = customer.wasteTypes.find((el) => el.code === wasteType)?.weight
    .unit;

  if (!wtValue) return "";

  return formatWeight(wtValue, { suffix: ` ${wtUnit}` as TFormatNumberSuffix });
};

const shouldUtilizeWasteType = (
  customer: Customer,
  wasteType: string,
  tenantCategories: FetchedTenantTypes,
): boolean => {
  let applicableCategories = tenantCategories.filter(
    (tc) => tc.id === customer.commercialCustomerCategory,
  );
  if (applicableCategories.length === 0) {
    applicableCategories = [tenantCategories.find((el) => el.id === "6")!];
  }
  const expectedWasteTypes = Array.from(
    new Set(
      applicableCategories.reduce((acc: string[], c) => {
        return [...acc, ...(c.expectedWasteTypes || []).map((t) => t.id)];
      }, []),
    ),
  );

  return expectedWasteTypes.some((el) => el === wasteType);
};

const getUtilizationColor = (
  customer: Customer,
  wasteType: string,
  expectedWasteTypes: FetchedTenantTypes,
) => {
  if (customer.wasteTypes.some((wt) => wasteType === wt.code)) {
    return "#F3F6C7";
  }

  if (shouldUtilizeWasteType(customer, wasteType, expectedWasteTypes)) {
    return "#FFE5D4";
  }

  return "";
};

const sortByWasteType =
  (wasteType: string, dir: number, expectedWasteTypes: FetchedTenantTypes) =>
  (c1: Customer, c2: Customer): number => {
    const t1WasteType = c1.wasteTypes.find((el) => el.code === wasteType);
    const t2WasteType = c2.wasteTypes.find((el) => el.code === wasteType);
    const t1ShouldUtilize = shouldUtilizeWasteType(
      c1,
      wasteType,
      expectedWasteTypes,
    );
    const t2ShouldUtilize = shouldUtilizeWasteType(
      c2,
      wasteType,
      expectedWasteTypes,
    );

    if (!t1WasteType && !t2WasteType) {
      if (t1ShouldUtilize && !t2ShouldUtilize) {
        return -1 * dir;
      }
      if (!t1ShouldUtilize && t2ShouldUtilize) {
        return 1 * dir;
      }
      return 0 * dir;
    }

    if (t1WasteType && !t2WasteType) {
      return -1 * dir;
    }

    if (!t1WasteType && t2WasteType) {
      return 1 * dir;
    }

    if (t1WasteType && t2WasteType) {
      return t1WasteType.weight.quantity > t2WasteType.weight.quantity
        ? -1 * dir
        : 1 * dir;
    }

    return 0 * dir;
  };

const sortTenantsByName = (c1: Customer, c2: Customer) =>
  c1.customerName > c2.customerName ? 1 : -1;

export const TenantWasteTypesCard: FC<{ realEstateId: string }> = ({
  realEstateId,
}) => {
  const { t } = useTrans();
  const [sortColumn, setSortColumn] = useState(-1); // -1 for customer column
  const [sortAscending, setSortAscending] = useState(true);

  const [
    { timeframe: [selectedTimeframe] = [], tenantCategory },
    setActiveOptions,
  ] = useState<ActiveOptions<"timeframe" | "tenantCategory">>({} as any);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [uniqueWasteTypes, setUniqueWasteTypes] = useState<string[]>([]);

  const { wasteTypes: sanityData, tenantCategories: allTenantCategories } =
    useCommonEntitiesStore();
  const { wasteTypeConfig, terminalConfig } = useConfigService();
  const { usedTenantCategories } = useMinimalCustomers();

  const defaultPeriod = useMemo(
    () => terminalConfig?.defaultTimeFrame || FilterTimeFrameValue.Last30Days,
    [terminalConfig],
  );

  const { startTimeISO, endTimeISO } = useMemo(() => {
    return getTimeFrame(
      selectedTimeframe?.value as FilterTimeFrameValue,
      defaultPeriod,
    );
  }, [selectedTimeframe, defaultPeriod]);

  const tenantCategoryValue = useMemo(() => {
    return hasNonAllOptionActive(tenantCategory)
      ? tenantCategory[0].value
      : undefined;
  }, [tenantCategory]);

  const { data, isLoading } = trpc.dwh.customers.get.useQuery(
    {
      startTimeISO,
      endTimeISO,
      commercialCustomerCategory: tenantCategoryValue,
      terminalId: realEstateId,
    },
    {
      queryKeyHashFn: () =>
        [
          "customers",
          startTimeISO,
          endTimeISO,
          tenantCategoryValue,
          realEstateId,
        ].toString(),
    },
  );

  const { data: accessCodes, isLoading: isLoadingAccessCodes } =
    trpc.scanner.customers.getAllAccessCodes.useQuery(
      {
        terminalId: realEstateId,
      },
      {
        queryKeyHashFn: () => ["customersAccessCodes", realEstateId].toString(),
      },
    );

  useEffect(() => {
    if (data) {
      setCustomers(data.customers.sort(sortTenantsByName));

      const wasteTypes = data.customers.reduce(
        (acc: string[], t) => [...acc, ...t.wasteTypes.map((wt) => wt.code)],
        [],
      );
      setUniqueWasteTypes(
        sortByOrderRank(Array.from(new Set(wasteTypes)), sanityData || []),
      );
    }
  }, [data, sanityData]);

  if (!sanityData) return <></>; // FIXME: Loading and error handling

  const filters: FilterSort[] = [
    createTimeFilters({
      id: "timeframe",
      defaultValue: defaultPeriod as FilterTimeFrameValue,
    }),
    {
      options: getTenantCategoryOptions(usedTenantCategories),
      title: "statisticsLabels:tenantCategory",
      id: "tenantCategory",
      type: "filter",
      menuType: FilterSortMenuType.Select,
      defaultValue: OPTION_ALL,
    },
  ];

  const handleColHeaderClick = (colIndex: number, wt?: string) => {
    const isCurrentCol = sortColumn === colIndex;
    const sortAsc = isCurrentCol ? !sortAscending : true;
    if (colIndex === -1) {
      customers.sort((a, b) =>
        a.customerName > b.customerName ? (sortAsc ? 1 : -1) : sortAsc ? -1 : 1,
      );
    }

    if (wt) {
      customers.sort(
        sortByWasteType(wt, sortAsc ? 1 : -1, allTenantCategories || []),
      );
    }

    setSortColumn(colIndex);
    setSortAscending(sortAsc);
  };

  return (
    <Card
      filters={filters}
      onFilterOptionChange={setActiveOptions}
      overflow="auto"
    >
      {!uniqueWasteTypes.length && !isLoading ? (
        <EmptyView type="noData" />
      ) : (
        <div className="flex h-full flex-col">
          <Legend />
          <div className="overflow-auto">
            {isLoading || isLoadingAccessCodes ? (
              <LoadingTable rows={25} />
            ) : (
              <table className="relative w-full table-auto">
                <thead>
                  <tr className="border-grey2 sticky top-0 z-9 h-[48px] cursor-pointer border-b bg-white text-center align-top font-medium whitespace-nowrap select-none">
                    <th
                      className="border-grey2 sticky left-0 border-b bg-white px-2 pt-1 text-sm sm:px-5"
                      onClick={() => handleColHeaderClick(-1)}
                    >
                      <div className="flex h-6 items-center gap-1.5">
                        {t("entities:customer")}
                        <ArrowUp
                          className={twMerge(
                            sortColumn !== -1 && "stroke-grey4",
                            sortColumn !== -1 && !sortAscending && "rotate-180",
                          )}
                        />
                      </div>
                    </th>
                    {uniqueWasteTypes.map((wt, colIndex) => {
                      const isSortedCol = sortColumn === colIndex;

                      const customWT = wasteTypeConfig.find(
                        (el) => el.wasteTypeCode === wt,
                      );

                      const title =
                        customWT?.name ||
                        sanityData.find((el) => el.id === wt)?.name ||
                        wt;

                      const tooltipUUID = v4();
                      return (
                        <th
                          className="h-10 px-1.5 pt-1 pb-3"
                          key={`wasteTypesTableHeaderCell_${colIndex}`}
                          onClick={() => handleColHeaderClick(colIndex, wt)}
                          data-tooltip-id={tooltipUUID}
                        >
                          <div className="flex items-center justify-center gap-1.5">
                            {customWT?.color ? (
                              <div
                                className="h-6 w-6 rounded-full"
                                style={{ backgroundColor: customWT.color }}
                              />
                            ) : (
                              <img
                                src={
                                  sanityData.find((el) => el.id === wt)
                                    ?.iconGrayscale as string
                                }
                                className="top-0 right-0 left-0 h-6 w-6"
                                alt={`${sanityData.find((el) => el.id === wt)?.name ?? wt}-icon`}
                              />
                            )}

                            <ArrowUp
                              className={twMerge(
                                !isSortedCol && "stroke-grey4",
                                (!isSortedCol || !sortAscending) &&
                                  "rotate-180",
                              )}
                            />
                          </div>

                          <Tooltip
                            id={tooltipUUID}
                            content={title}
                            className="z-10"
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {customers.map((t, i) => {
                    const tenantCode =
                      accessCodes?.data.find(
                        (d) => d.storeUuid === t.customerId,
                      )?.accessCode || "";

                    return (
                      <tr
                        className="group border-grey3 hover:bg-hover-color h-[48px] border-b text-center text-xs"
                        key={`wasteTypesTableRow_${i}`}
                      >
                        <td className="group-hover:bg-hover-color sticky left-0 max-w-[100px] overflow-hidden bg-white px-2 py-1 text-left text-sm text-ellipsis whitespace-nowrap sm:w-auto sm:max-w-none sm:px-5">
                          <div className="flex items-center">
                            {t.customerName}
                            {tenantCode && (
                              <ShareLink link={getTenantUrl(tenantCode)} />
                            )}
                          </div>
                        </td>
                        {uniqueWasteTypes.map((wt, colIndex) => (
                          <td
                            className="border-grey3 group-hover:bg-hover-color border px-1.5 py-1"
                            key={`wasteTypesTableRow_${i}_cell_${colIndex}`}
                            style={{
                              backgroundColor: getUtilizationColor(
                                t,
                                wt,
                                allTenantCategories || [],
                              ),
                            }}
                          >
                            {printWasteType(t, wt)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export interface ITenantWasteTypesCardEntry {}

const Legend = () => {
  const { t } = useTrans();
  return (
    <div className="mt-3 mb-4 flex gap-3 self-end text-xs">
      <div className="flex items-center gap-1.5">
        <div className="bg-light-peach h-6 w-12" />
        {t("errors:notUtilized")}
      </div>
      <div className="flex items-center gap-1.5">
        <div className="bg-light-lichen h-6 w-12" />
        {t("genericLabels:utilized")}
      </div>
      <div className="flex items-center gap-1.5">
        <div className="h-6 w-12 border" />
        {t("errors:notExpected")}
      </div>
    </div>
  );
};
